import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { X, Star, Trophy } from "lucide-react";
import { API } from "../context";

// Explains exactly how Explorer Levels work: everyone starts at Level 1 and
// levels up by earning Stars. Shows the full threshold table (fetched live so
// it always matches the backend) plus the player's current standing.
export default function LevelInfoModal({ open, onClose, current }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open || data) return;
    axios.get(`${API}/arcade/levels`).then((r) => setData(r.data)).catch(() => {});
  }, [open, data]);

  if (!open) return null;
  const levels = data?.levels || [];
  const curLevel = current?.level || 1;
  const curStars = current?.total_stars_earned ?? current?.stars ?? 0;

  return createPortal(
    <div className="fixed inset-0 z-[2147483500] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
      onClick={onClose} data-testid="level-info-modal">
      <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl bg-[#0f1530] border border-white/10 shadow-2xl"
        onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 bg-[#0f1530] flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h2 className="text-white font-fredoka font-bold text-lg flex items-center gap-2">
            <Trophy size={18} className="text-yellow-300" /> How Levels Work
          </h2>
          <button onClick={onClose} className="text-white/60 hover:text-white" data-testid="level-info-close">
            <X size={20} />
          </button>
        </div>

        <div className="px-5 py-4">
          <p className="text-white/70 text-sm font-nunito leading-relaxed mb-3">
            Everyone starts at <b className="text-white">Level 1</b>. You level up by earning
            <span className="text-yellow-300 font-bold"> Stars</span> from games, lessons, and activities.
            The more Stars you collect, the higher your level climbs. Levels <b className="text-white">6 to 10</b> are
            the big goals to reach, and you can keep going past Level 10 forever!
          </p>

          {current && (
            <div className="flex items-center gap-3 rounded-xl bg-white/5 border border-white/10 px-4 py-2.5 mb-4" data-testid="level-info-current">
              <span className="text-white font-fredoka font-bold">Level {curLevel}</span>
              <span className="text-white/40 text-sm">{current?.level_name || ""}</span>
              <span className="ml-auto flex items-center gap-1 text-yellow-300 text-sm font-bold">
                <Star size={12} fill="currentColor" /> {curStars.toLocaleString()}
              </span>
            </div>
          )}

          <div className="space-y-1.5">
            {levels.map((lv) => {
              const reached = curStars >= lv.stars_required;
              const isCurrent = lv.level === curLevel;
              return (
                <div key={lv.level}
                  className={`flex items-center gap-3 rounded-lg px-3 py-2 border ${
                    isCurrent ? "bg-emerald-500/15 border-emerald-400/40"
                    : reached ? "bg-white/5 border-white/10" : "bg-white/[0.02] border-white/5"}`}
                  data-testid={`level-row-${lv.level}`}>
                  <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${
                    reached ? "bg-yellow-400 text-blue-900" : "bg-white/10 text-white/50"}`}>
                    {lv.level}
                  </span>
                  <span className={`flex-1 text-sm font-bold ${reached ? "text-white" : "text-white/50"}`}>{lv.name}</span>
                  <span className="flex items-center gap-1 text-xs text-white/60">
                    <Star size={10} className="text-yellow-300" fill="currentColor" />
                    {lv.stars_required.toLocaleString()}
                  </span>
                </div>
              );
            })}
            {data && (
              <p className="text-white/45 text-xs font-nunito pt-2">
                Past Level {data.max_named_level}: every {data.beyond_step.toLocaleString()} more Stars earns
                another level as a <b className="text-white/70">{data.beyond_name}</b>.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
